<script setup lang="ts">
import { computed } from 'vue';
import Icon from '../icon/icon.vue';
import { IconName, IconSize } from '../icon/icon.constants';
import type { usePdfRenderer } from './use-pdf-renderer';

type Props = {
  /** Renderer instance shared with `PdfViewerCanvas`. */
  renderer: ReturnType<typeof usePdfRenderer>;
};

const props = defineProps<Props>();

const zoomPercent = computed(() =>
  Math.round(props.renderer.zoomMultiplier.value * 100),
);

const onPageInput = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  await props.renderer.goToPage(Number(target.value));
  target.value = String(props.renderer.currentPage.value);
};
</script>

<template>
  <div class="flex items-center gap-xl sm:gap-2xl">
    <div v-if="renderer.totalPages.value > 1" class="flex items-center gap-md">
      <button
        type="button"
        class="flex items-center justify-center rounded-sm border border-line p-sm text-on-surface-primary transition-colors hover:bg-surface-container-hover disabled:cursor-not-allowed disabled:opacity-30"
        :disabled="renderer.currentPage.value <= 1"
        aria-label="Previous page"
        @click="renderer.previousPage()"
      >
        <Icon :name="IconName.CHEVRON_LEFT" :size="IconSize.LG" />
      </button>
      <input
        type="number"
        min="1"
        :max="renderer.totalPages.value || 1"
        :value="renderer.currentPage.value"
        class="w-6xl rounded-sm border border-primary bg-surface-container p-sm text-center text-sm font-bold text-on-surface-primary outline-none"
        aria-label="Current page"
        @change="onPageInput"
      />
      <span
        class="min-w-[46px] text-center text-sm font-bold text-on-surface-primary"
      >
        / {{ renderer.totalPages.value }}
      </span>
      <button
        type="button"
        class="flex items-center justify-center rounded-sm border border-line p-sm text-on-surface-primary transition-colors hover:bg-surface-container-hover disabled:cursor-not-allowed disabled:opacity-30"
        :disabled="
          renderer.currentPage.value >= (renderer.totalPages.value || 1)
        "
        aria-label="Next page"
        @click="renderer.nextPage()"
      >
        <Icon :name="IconName.CHEVRON_RIGHT" :size="IconSize.LG" />
      </button>
    </div>

    <div
      class="flex items-center gap-md rounded-sm border border-line px-md py-sm"
    >
      <button
        type="button"
        class="flex items-center justify-center rounded-sm p-sm text-on-surface-secondary transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-30"
        :disabled="renderer.zoomMultiplier.value >= 3"
        aria-label="Zoom in"
        @click="renderer.zoomIn()"
      >
        <Icon :name="IconName.PLUS" :size="IconSize.LG" />
      </button>
      <span
        class="min-w-[56px] text-center text-sm font-bold text-on-surface-secondary"
      >
        {{ zoomPercent }}%
      </span>
      <button
        type="button"
        class="flex items-center justify-center rounded-sm p-sm text-on-surface-secondary transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-30"
        :disabled="renderer.zoomMultiplier.value <= 0.5"
        aria-label="Zoom out"
        @click="renderer.zoomOut()"
      >
        <Icon :name="IconName.MINUS" :size="IconSize.LG" />
      </button>
    </div>
  </div>
</template>
